import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CategoryRuleSelector from "./CategoryRuleSelector";
import SettingsCourtGroups from "./SettingsCourtGroups";
import type { CustomerCategory } from "@/hooks/useCustomerCategories";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface Props {
  settings: Record<string, unknown>;
  updateSetting: (key: string, value: unknown) => void;
  hall: Record<string, unknown> | null;
  updateHall: (field: string, value: unknown) => void;
  categories?: CustomerCategory[];
  onUpdateCategoryRules?: (id: string, rules: Record<string, unknown> | null) => void;
  org?: Record<string, unknown>;
  updateOrgField?: (field: string, value: unknown) => void;
}

export default function SettingsBookingRules({ settings, updateSetting, hall, updateHall, categories = [], onUpdateCategoryRules, org, updateOrgField }: Props) {
  const { t } = useTranslation("admin");
  const [selectedCatId, setSelectedCatId] = useState("global");
  const booking = (settings.booking as Record<string, unknown>) || {};
  const selectedCat = categories.find((c) => c.id === selectedCatId);
  const catRules = selectedCat?.booking_rules as Record<string, unknown> | null;
  const isOverride = selectedCatId !== "global";

  const updateBooking = (key: string, value: unknown) => {
    if (isOverride && selectedCat && onUpdateCategoryRules) {
      const current = catRules || {};
      onUpdateCategoryRules(selectedCat.id, { ...current, [key]: value });
    } else {
      updateSetting("booking", { ...booking, [key]: value });
    }
  };

  const clearOverride = (key: string) => {
    if (!selectedCat || !onUpdateCategoryRules || !catRules) return;
    const { [key]: _, ...rest } = catRules;
    onUpdateCategoryRules(selectedCat.id, Object.keys(rest).length > 0 ? rest : null);
  };

  const getValue = (key: string, defaultVal: any) => {
    if (isOverride && catRules && catRules[key] !== undefined) return catRules[key];
    return booking[key] ?? defaultVal;
  };

  const isOverridden = (key: string) => isOverride && catRules && catRules[key] !== undefined;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-1">{t("settings.bookingRules.title")}</h3>
          <p className="text-sm text-muted-foreground mb-4">{t("settings.bookingRules.description")}</p>
        </div>
        {categories.length > 0 && (
          <CategoryRuleSelector categories={categories} selected={selectedCatId} onSelect={setSelectedCatId} />
        )}
      </div>

      {isOverride && (
        <p className="text-xs text-muted-foreground bg-muted/50 rounded-md p-2">
          {t("settings.bookingRules.showingRulesFor")} <strong>{selectedCat?.name}</strong>. {t("settings.bookingRules.emptyFieldsInherit")}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <OverridableField label={t("settings.bookingRules.fieldMinSlots")} isOverride={isOverride} isOverridden={!!isOverridden("min_slots")} onClear={() => clearOverride("min_slots")}>
          <NumberInput min={1} max={8} value={getValue("min_slots", 2)} onValueChange={(v) => updateBooking("min_slots", v)} />
          <p className="text-xs text-muted-foreground">= {(getValue("min_slots", 2)) * 30} minuter</p>
        </OverridableField>
        <OverridableField label={t("settings.bookingRules.fieldMaxSlots")} isOverride={isOverride} isOverridden={!!isOverridden("max_slots")} onClear={() => clearOverride("max_slots")}>
          <NumberInput min={1} max={12} value={getValue("max_slots", 4)} onValueChange={(v) => updateBooking("max_slots", v)} />
          <p className="text-xs text-muted-foreground">= {(getValue("max_slots", 4)) * 30} minuter</p>
        </OverridableField>
        <OverridableField label={t("settings.bookingRules.fieldMaxActiveBookings")} isOverride={isOverride} isOverridden={!!isOverridden("max_active_bookings")} onClear={() => clearOverride("max_active_bookings")}>
          {isOverride ? (
            <NumberInput min={1} value={getValue("max_active_bookings", hall?.max_active_bookings ?? 3)} onValueChange={(v) => updateBooking("max_active_bookings", v)} />
          ) : (
            <NumberInput min={1} value={hall?.max_active_bookings ?? 3} onValueChange={(v) => updateHall("max_active_bookings", v)} />
          )}
        </OverridableField>
        <OverridableField label={t("settings.bookingRules.fieldAdvanceDays")} isOverride={isOverride} isOverridden={!!isOverridden("advance_days")} onClear={() => clearOverride("advance_days")}>
          <NumberInput min={1} max={90} value={getValue("advance_days", 14)} onValueChange={(v) => updateBooking("advance_days", v)} />
        </OverridableField>
      </div>

      {!isOverride && (
        <>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <Label>{t("settings.bookingRules.requireAccount")}</Label>
              <p className="text-xs text-muted-foreground">{t("settings.bookingRules.requireAccountDesc")}</p>
            </div>
            <Switch checked={booking.require_account ?? true} onCheckedChange={(v) => updateBooking("require_account", v)} />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <Label>{t("settings.bookingRules.allowReschedule")}</Label>
              <p className="text-xs text-muted-foreground">{t("settings.bookingRules.allowRescheduleDesc")}</p>
            </div>
            <Switch checked={getValue("allow_self_reschedule", true)} onCheckedChange={(v) => updateBooking("allow_self_reschedule", v)} />
          </div>

          {getValue("allow_self_reschedule", true) && (
            <div className="flex items-center gap-3 rounded-lg border p-4">
              <div className="flex-1">
                <Label>{t("settings.bookingRules.rescheduleDeadlineLabel")}</Label>
                <p className="text-xs text-muted-foreground">{t("settings.bookingRules.rescheduleDeadlineHint")}</p>
              </div>
              <NumberInput
                min={0}
                max={72}
                value={getValue("reschedule_deadline_hours", 12)}
                onValueChange={(v) => updateBooking("reschedule_deadline_hours", v || 12)}
                className="w-24 h-8 text-sm"
              />
            </div>
          )}

          <div className="flex items-center gap-2 rounded-lg border p-4">
            <div className="flex-1">
              <Label>{t("settings.bookingRules.slotInterval")}</Label>
              <p className="text-xs text-muted-foreground">{t("settings.bookingRules.slotIntervalDesc")}</p>
            </div>
            <Select value={String(hall?.time_step_minutes ?? 30)} onValueChange={(v) => updateHall("time_step_minutes", parseInt(v))}>
              <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 min</SelectItem>
                <SelectItem value="30">30 min</SelectItem>
                <SelectItem value="60">60 min</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Gap protection / anti-vaskning */}
          <div className="space-y-4 rounded-lg border p-4">
            <div>
              <h4 className="font-medium">{t("settings.bookingRules.gapProtection")}</h4>
              <p className="text-xs text-muted-foreground">
                {t("settings.bookingRules.gapProtectionDesc")}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>{t("settings.bookingRules.enableGapProtection")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("settings.bookingRules.enableGapProtectionDesc")}
                </p>
              </div>
              <Switch
                checked={getValue("gap_protection", false)}
                onCheckedChange={(v) => updateBooking("gap_protection", v)}
              />
            </div>

            {getValue("gap_protection", false) && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs">{t("settings.bookingRules.fieldMinGap")}</Label>
                  <NumberInput
                    min={0}
                    step={15}
                    value={getValue("min_gap_minutes", 60)}
                    onValueChange={(v) => updateBooking("min_gap_minutes", v || 60)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settings.bookingRules.minGapHint")}
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t("settings.bookingRules.fixedStartTimes")}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t("settings.bookingRules.fixedStartTimesDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={getValue("fixed_start_times", false)}
                    onCheckedChange={(v) => updateBooking("fixed_start_times", v)}
                  />
                </div>

                {getValue("fixed_start_times", false) && (
                  <div className="space-y-2">
                    <Label className="text-xs">{t("settings.bookingRules.fieldStartInterval")}</Label>
                    <div className="flex gap-2">
                      {[30, 60].map((v) => (
                        <button
                          key={v}
                          className={cn(
                            "px-3 py-1.5 rounded text-xs font-medium transition-colors",
                            getValue("start_interval_minutes", 60) === v
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80"
                          )}
                          onClick={() => updateBooking("start_interval_minutes", v)}
                        >
                          {t("settings.bookingRules.everyNMin", { n: v })}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("settings.bookingRules.startIntervalHint")}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t("settings.bookingRules.fixedDurations")}</Label>
                    <p className="text-xs text-muted-foreground">
                      {t("settings.bookingRules.fixedDurationsDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={getValue("fixed_durations", false)}
                    onCheckedChange={(v) => updateBooking("fixed_durations", v)}
                  />
                </div>

                {getValue("fixed_durations", false) && (
                  <div className="space-y-2">
                    <Label className="text-xs">{t("settings.bookingRules.fieldAllowedDurations")}</Label>
                    <div className="flex gap-2 flex-wrap">
                      {[60, 90, 120].map((d) => {
                        const allowed = (getValue("allowed_durations", [60, 90, 120]) as number[]);
                        const isSelected = allowed.includes(d);
                        return (
                          <button
                            key={d}
                            className={cn(
                              "px-3 py-1.5 rounded text-xs font-medium transition-colors",
                              isSelected
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                            )}
                            onClick={() => {
                              const next = isSelected
                                ? allowed.filter((x) => x !== d)
                                : [...allowed, d].sort();
                              if (next.length > 0) updateBooking("allowed_durations", next);
                            }}
                          >
                            {d} min
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Multi-select limits */}
          {org && updateOrgField && (
            <div className="space-y-4 rounded-lg border p-4">
              <div>
                <h4 className="font-medium">{t("multiSelectLimits.title")}</h4>
                <p className="text-xs text-muted-foreground">
                  {t("multiSelectLimits.description")}
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("multiSelectLimits.maxSlotsPerBooking")}</Label>
                  <NumberInput
                    min={1}
                    max={20}
                    value={org.max_slots_per_booking ?? 4}
                    onValueChange={(v) => updateOrgField("max_slots_per_booking", v || 4)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("multiSelectLimits.maxConcurrentBookings")}</Label>
                  <NumberInput
                    min={1}
                    max={50}
                    value={org.max_concurrent_bookings ?? 3}
                    onValueChange={(v) => updateOrgField("max_concurrent_bookings", v || 3)}
                  />
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-3">{t("multiSelectLimits.memberOverrides")}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t("multiSelectLimits.memberMaxSlotsPerBooking")}</Label>
                    <NumberInput
                      min={1}
                      max={20}
                      value={org.member_max_slots_per_booking ?? 8}
                      onValueChange={(v) => updateOrgField("member_max_slots_per_booking", v || 8)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("multiSelectLimits.memberMaxConcurrentBookings")}</Label>
                    <NumberInput
                      min={1}
                      max={100}
                      value={org.member_max_concurrent_bookings ?? 10}
                      onValueChange={(v) => updateOrgField("member_max_concurrent_bookings", v || 10)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Court assignment mode */}
      {hall && (
        <div className="space-y-3 pt-4 border-t">
          <h4 className="text-sm font-semibold">{t("settings.bookingRules.courtAssignmentMode")}</h4>
          <div className="space-y-2">
            {([
              { value: "manual", label: t("settings.bookingRules.modeManual"), desc: t("settings.bookingRules.modeManualDesc") },
              { value: "auto", label: t("settings.bookingRules.modeAuto"), desc: t("settings.bookingRules.modeAutoDesc") },
              { value: "by_category", label: t("settings.bookingRules.modeByCategory"), desc: t("settings.bookingRules.modeByCategoryDesc") },
            ] as const).map((opt) => {
              const selected = (hall.court_assignment_mode as string ?? "manual") === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateHall("court_assignment_mode", opt.value)}
                  className={cn(
                    "w-full text-left rounded-lg border-2 p-3 transition-all",
                    selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                  )}
                >
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-xs text-muted-foreground">{opt.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Court groups (only for by_category mode) */}
      {hall && (hall.court_assignment_mode as string) === "by_category" && hall.id && org?.id && (
        <SettingsCourtGroups hallId={hall.id as string} organizationId={org.id as string} />
      )}

      {/* Free booking settings */}
      {hall && (
        <div className="space-y-3 pt-4 border-t">
          <h4 className="text-sm font-semibold">{t("settings.bookingRules.freeBookings")}</h4>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">{t("settings.bookingRules.allowFreeBookings")}</Label>
              <p className="text-xs text-muted-foreground">{t("settings.bookingRules.allowFreeBookingsDesc")}</p>
            </div>
            <Switch
              checked={(hall.allow_free_bookings as boolean) ?? true}
              onCheckedChange={(v) => updateHall("allow_free_bookings", v)}
            />
          </div>
          {((hall.allow_free_bookings as boolean) ?? true) && (
            <div className="space-y-2">
              <Label className="text-sm">{t("settings.bookingRules.freeBookingReasons")}</Label>
              <div className="space-y-1">
                {((hall.free_booking_reasons as string[]) ?? ["Personal", "Underhåll", "Event", "Annat"]).map((reason, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={reason}
                      onChange={(e) => {
                        const reasons = [...((hall.free_booking_reasons as string[]) ?? ["Personal", "Underhåll", "Event", "Annat"])];
                        reasons[idx] = e.target.value;
                        updateHall("free_booking_reasons", reasons);
                      }}
                      className="h-8 text-sm flex-1"
                    />
                    <button
                      onClick={() => {
                        const reasons = [...((hall.free_booking_reasons as string[]) ?? ["Personal", "Underhåll", "Event", "Annat"])];
                        reasons.splice(idx, 1);
                        updateHall("free_booking_reasons", reasons);
                      }}
                      className="text-xs text-destructive hover:underline shrink-0"
                    >
                      {t("settings.bookingRules.btnRemove")}
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  const reasons = [...((hall.free_booking_reasons as string[]) ?? ["Personal", "Underhåll", "Event", "Annat"]), t("settings.bookingRules.newReason")];
                  updateHall("free_booking_reasons", reasons);
                }}
                className="text-xs text-primary hover:underline"
              >
                + {t("settings.bookingRules.btnAddReason")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Betalning vid bokning */}
      <div className="border-t pt-4">
        <h4 className="font-medium text-sm mb-3">{t("settings.bookingRules.paymentSection")}</h4>
        <div className="flex items-center justify-between">
          <div>
            <Label>{t("settings.bookingRules.requirePayment")}</Label>
            <p className="text-xs text-muted-foreground">{t("settings.bookingRules.requirePaymentDesc")}</p>
          </div>
          <Switch
            checked={(settings.booking_require_payment as boolean) ?? true}
            onCheckedChange={(v) => updateSetting("booking_require_payment", v)}
          />
        </div>
      </div>
    </div>
  );
}

function OverridableField({ label, isOverride, isOverridden, onClear, children }: {
  label: string;
  isOverride: boolean;
  isOverridden: boolean;
  onClear: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation("admin");
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label>{label}</Label>
        {isOverride && isOverridden && (
          <button onClick={onClear} className="text-[10px] text-primary underline">{t("settings.bookingRules.btnReset")}</button>
        )}
      </div>
      {children}
    </div>
  );
}
